import {
  Activity,
  BookOpen,
  CheckSquare,
  Cpu,
  FileText,
  GitBranch,
  KeyRound,
  LayoutDashboard,
  Plug,
  ScrollText,
  Server,
  Shield,
  UserRound,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

/**
 * Control-plane information architecture (docs/11-UX-UI.md). Sections appear in navigation as
 * soon as the IA is agreed, but a section's screen is built only in the increment that needs
 * it; until then it renders a "planned" placeholder stating the increment.
 */
export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  /** Roadmap increment in which the screen becomes functional (docs/15-ROADMAP.md). */
  increment: number;
  available: boolean;
  summary: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    label: 'Operate',
    items: [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: LayoutDashboard,
        increment: 1,
        available: true,
        summary: 'Operational overview: platform health, backups, recent executions.',
      },
      {
        label: 'Executions',
        path: '/executions',
        icon: Activity,
        increment: 1,
        available: true,
        summary: 'Every workflow run with its full, inspectable trace.',
      },
      {
        label: 'Approvals',
        path: '/approvals',
        icon: CheckSquare,
        increment: 5,
        available: true,
        summary: 'Human-in-the-loop decisions waiting for a reviewer.',
      },
    ],
  },
  {
    label: 'Knowledge',
    items: [
      {
        label: 'Knowledge',
        path: '/knowledge',
        icon: BookOpen,
        increment: 3,
        available: true,
        summary: 'Sources, items, candidates, conflicts and provenance.',
      },
    ],
  },
  {
    label: 'Build',
    items: [
      {
        label: 'Workflows',
        path: '/workflows',
        icon: Workflow,
        increment: 5,
        available: true,
        summary: 'Versioned workflow definitions composed from registered step types.',
      },
      {
        label: 'Prompts',
        path: '/prompts',
        icon: FileText,
        increment: 2,
        available: true,
        summary: 'Versioned prompts with evaluation before publication.',
      },
    ],
  },
  {
    label: 'Integrate',
    items: [
      {
        label: 'Connections',
        path: '/connections',
        icon: GitBranch,
        increment: 2,
        available: true,
        summary: 'Named connections mapping capabilities to MCP or native implementations.',
      },
      {
        label: 'MCP Registry',
        path: '/mcp-registry',
        icon: Plug,
        increment: 2,
        available: true,
        summary: 'Outbound MCP servers, discovered tools and capability approvals.',
      },
      {
        label: 'Credentials',
        path: '/credentials',
        icon: KeyRound,
        increment: 1,
        available: true,
        summary: 'Write-only, envelope-encrypted credentials. Values are never shown again.',
      },
    ],
  },
  {
    label: 'Govern',
    items: [
      {
        label: 'Policies',
        path: '/policies',
        icon: Shield,
        increment: 5,
        available: true,
        summary: 'Authorization and egress policies (RBAC/ABAC, classification eligibility).',
      },
      {
        label: 'Models & budgets',
        path: '/llm',
        icon: Cpu,
        increment: 2,
        available: true,
        summary:
          'LLM providers, logical model profiles, data-classification eligibility and budgets.',
      },
      {
        label: 'Audit',
        path: '/audit',
        icon: ScrollText,
        increment: 1,
        available: true,
        summary: 'Tamper-evident audit log of security- and knowledge-relevant actions.',
      },
      {
        label: 'System Health',
        path: '/system/health',
        icon: Server,
        increment: 1,
        available: true,
        summary: 'Liveness and readiness of platform services.',
      },
      {
        label: 'Session',
        path: '/session',
        icon: UserRound,
        increment: 1,
        available: true,
        summary: 'Current user and session.',
      },
    ],
  },
];

export const ALL_NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);
